import { useUIColors } from '@/core/hooks';
import { visualPxToLayoutPx } from '@/core/utils/viewportMetrics';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

const THUMB_WIDTH = 4;
const THUMB_INSET = 3;
const THUMB_MIN_HEIGHT = 24;
const FADE_IN_MS = 120;
const FADE_OUT_MS = 300;
const HIDE_DELAY_MS = 900;
/** Content inset that keeps an always-visible thumb off the content. */
const THUMB_CONTENT_GAP = 8;
const THUMB_GUTTER = THUMB_WIDTH + THUMB_INSET + THUMB_CONTENT_GAP;
const IDLE_OPACITY = 0.35;
const ACTIVE_OPACITY = 0.55;
/** Grab area around the 4px thumb so it is easy to hit with a mouse. */
const THUMB_HIT_WIDTH = 14;
const IS_WEB = Platform.OS === 'web';

interface DragState {
  startClientY: number;
  startOffset: number;
}

export interface OverlayScrollViewProps {
  /** Wrapper (sizing in the parent layout). */
  style?: StyleProp<ViewStyle>;
  /**
   * Inner ScrollView; replaces the content-hugging default. Not merged: RN Web
   * lets a default `flexGrow: 0` override a caller's `flex: 1` and collapse it.
   */
  scrollStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Keep the thumb on screen whenever the content can scroll (no fade-out); reserves a right gutter. */
  alwaysVisible?: boolean;
  nestedScrollEnabled?: boolean;
  scrollEventThrottle?: number;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  onContentSizeChange?: (width: number, height: number) => void;
  children: React.ReactNode;
}

/**
 * Material-style scroll: the native scrollbar is hidden and a thin thumb floats
 * over the content. By default it fades in on scroll/hover and out shortly after;
 * `alwaysVisible` keeps it shown and only brightens it while scrolling.
 */
export function OverlayScrollView({
  style,
  scrollStyle,
  contentContainerStyle,
  alwaysVisible = false,
  nestedScrollEnabled = true,
  scrollEventThrottle = 16,
  onScroll,
  onLayout,
  onContentSizeChange,
  children,
}: Readonly<OverlayScrollViewProps>) {
  const colors = useUIColors();
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const restingOpacity = alwaysVisible ? IDLE_OPACITY : 0;
  const peakOpacity = alwaysVisible ? ACTIVE_OPACITY : IDLE_OPACITY;
  const opacity = useRef(new Animated.Value(restingOpacity)).current;
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const dragRef = useRef<DragState | null>(null);
  const offsetYRef = useRef(0);
  const geometryRef = useRef({ maxOffset: 0, thumbTravel: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    opacity.setValue(restingOpacity);
  }, [opacity, restingOpacity]);

  const scheduleRest = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    if (dragRef.current) {
      return;
    }
    hideTimerRef.current = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: restingOpacity,
        duration: FADE_OUT_MS,
        useNativeDriver: false,
      }).start();
    }, HIDE_DELAY_MS);
  }, [opacity, restingOpacity]);

  const reveal = useCallback(() => {
    Animated.timing(opacity, {
      toValue: peakOpacity,
      duration: FADE_IN_MS,
      useNativeDriver: false,
    }).start();
    scheduleRest();
  }, [opacity, peakOpacity, scheduleRest]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      offsetYRef.current = event.nativeEvent.contentOffset.y;
      setOffsetY(event.nativeEvent.contentOffset.y);
      reveal();
      onScroll?.(event);
    },
    [reveal, onScroll],
  );

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      setViewportHeight(event.nativeEvent.layout.height);
      onLayout?.(event);
    },
    [onLayout],
  );

  const handleContentSizeChange = useCallback(
    (width: number, height: number) => {
      setContentHeight(height);
      onContentSizeChange?.(width, height);
    },
    [onContentSizeChange],
  );

  const scrollable = viewportHeight > 0 && contentHeight > viewportHeight + 1;
  const trackHeight = viewportHeight - THUMB_INSET * 2;
  const thumbHeight = scrollable
    ? Math.max(THUMB_MIN_HEIGHT, (viewportHeight / contentHeight) * trackHeight)
    : 0;
  const maxOffset = Math.max(1, contentHeight - viewportHeight);
  const progress = Math.min(1, Math.max(0, offsetY / maxOffset));
  const thumbTop = THUMB_INSET + progress * (trackHeight - thumbHeight);
  geometryRef.current = {
    maxOffset: Math.max(0, contentHeight - viewportHeight),
    thumbTravel: trackHeight - thumbHeight,
  };

  useEffect(() => {
    if (!isDragging || typeof window === 'undefined') {
      return undefined;
    }
    const handleMouseMove = (event: MouseEvent) => {
      const drag = dragRef.current;
      const { maxOffset: limit, thumbTravel } = geometryRef.current;
      if (!drag || thumbTravel <= 0) {
        return;
      }
      const deltaPx = visualPxToLayoutPx(event.clientY - drag.startClientY);
      const nextOffset = drag.startOffset + (deltaPx * limit) / thumbTravel;
      scrollRef.current?.scrollTo({
        y: Math.min(limit, Math.max(0, nextOffset)),
        animated: false,
      });
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      scheduleRest();
    };
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scheduleRest]);

  const handleThumbMouseDown = useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragRef.current = { startClientY: event.clientY, startOffset: offsetYRef.current };
      setIsDragging(true);
      reveal();
    },
    [reveal],
  );

  const thumb = (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.thumb,
        IS_WEB ? styles.thumbInHitArea : { top: thumbTop, height: thumbHeight },
        {
          backgroundColor: colors.fontPrimary,
          opacity,
        },
      ]}
    />
  );

  return (
    <View
      style={[styles.container, style]}
      {...({ onMouseEnter: scrollable ? reveal : undefined } as object)}
    >
      <ScrollView
        ref={scrollRef}
        style={scrollStyle ?? styles.scroll}
        contentContainerStyle={
          alwaysVisible ? [contentContainerStyle, styles.contentGutter] : contentContainerStyle
        }
        nestedScrollEnabled={nestedScrollEnabled}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={scrollEventThrottle}
        onScroll={handleScroll}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
      >
        {children}
      </ScrollView>
      {scrollable && IS_WEB ? (
        <View
          style={[
            styles.thumbHitArea,
            { top: thumbTop, height: thumbHeight },
            webCursor(isDragging ? 'grabbing' : 'grab'),
          ]}
          {...({ onMouseDown: handleThumbMouseDown } as object)}
        >
          {thumb}
        </View>
      ) : null}
      {scrollable && !IS_WEB ? thumb : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  contentGutter: {
    paddingRight: THUMB_GUTTER,
  },
  thumb: {
    position: 'absolute',
    right: THUMB_INSET,
    width: THUMB_WIDTH,
    borderRadius: THUMB_WIDTH / 2,
  },
  thumbHitArea: {
    position: 'absolute',
    right: 0,
    width: THUMB_HIT_WIDTH,
  },
  thumbInHitArea: {
    top: 0,
    bottom: 0,
  },
});

function webCursor(cursor: 'grab' | 'grabbing'): ViewStyle {
  return { cursor } as unknown as ViewStyle;
}
